import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { TrendingUp, FileText, Users, Shield, Globe, Building2 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cb6f0404-c085-4dfb-8e59-eed26caf1532/artifacts/mfp1ilrx_MinoLedger%20logo.jpg';

export function LandingPage() {
  const { login } = useAuth();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MinoLedger" className="w-10 h-10 rounded-lg object-contain" />
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MinoLedger
            </span>
          </div>
          <Button 
            data-testid="login-btn"
            onClick={login}
            className="btn-brand rounded-lg px-6"
          >
            Inloggen met Google
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 
              className="text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Multi-entity boekhouding voor{' '}
              <span className="text-gradient">internationale ondernemers</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Beheer uw Nederlandse BV's en Amerikaanse C-Corp vanuit één platform. 
              Volledig compliant met Europese en Amerikaanse regelgeving.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                data-testid="get-started-btn"
                onClick={login}
                className="btn-brand rounded-lg px-8 py-3 text-lg"
              >
                Start gratis
              </Button>
              <Button 
                variant="outline" 
                className="rounded-lg px-8 py-3 text-lg"
              >
                Bekijk demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 
            className="text-3xl font-semibold text-center mb-12"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Alles wat u nodig heeft
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Building2 className="w-6 h-6" />}
              title="Multi-entity beheer"
              description="Beheer Nederlandse BV's en Amerikaanse C-Corps met holding structuren."
            />
            <FeatureCard 
              icon={<TrendingUp className="w-6 h-6" />}
              title="Financiële overzichten"
              description="Automatische balans, W&V-rekening en kasstroomoverzichten."
            />
            <FeatureCard 
              icon={<FileText className="w-6 h-6" />}
              title="BTW Aangiftes"
              description="Genereer kwartaal BTW aangiftes voor Nederlandse entiteiten."
            />
            <FeatureCard 
              icon={<Users className="w-6 h-6" />}
              title="Cap Table & Opties"
              description="Beheer aandeelhouders en medewerker optieregelingen."
            />
            <FeatureCard 
              icon={<Shield className="w-6 h-6" />}
              title="Compliant"
              description="Voldoet aan Nederlandse en Amerikaanse boekhoudregels."
            />
            <FeatureCard 
              icon={<Globe className="w-6 h-6" />}
              title="Meertalig"
              description="Nederlandse sectie in NL of EN, Amerikaanse sectie in Engels."
            />
          </div>
        </div>
      </section>

      {/* Entities preview */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🇳🇱</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Nederlandse BV's
                  </h3>
                  <p className="text-sm text-muted-foreground">Inclusief holding structuur</p>
                </div>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6EC998] rounded-full" />
                  Kwartaal BTW aangiftes (21%)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6EC998] rounded-full" />
                  KVK-compliant boekhouding
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6EC998] rounded-full" />
                  ABN AMRO bankafschriften importeren
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#6EC998] rounded-full" />
                  Nederlandse notulen genereren
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🇺🇸</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    US C-Corporation
                  </h3>
                  <p className="text-sm text-muted-foreground">Delaware incorporation</p>
                </div>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#8387e7] rounded-full" />
                  Form 1120 invulinstructies
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#8387e7] rounded-full" />
                  State sales tax tracking
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#8387e7] rounded-full" />
                  Bank of America import
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#8387e7] rounded-full" />
                  English meeting minutes
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #6EC998, #8387e7)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl font-semibold mb-4 text-white"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Begin vandaag nog
          </h2>
          <p className="text-white/80 mb-8">
            Krijg direct inzicht in uw internationale bedrijfsstructuur.
          </p>
          <Button 
            data-testid="cta-login-btn"
            onClick={login}
            className="bg-white text-[#0F172A] hover:bg-white/90 rounded-lg px-8 py-3 text-lg"
          >
            Inloggen met Google
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="MinoLedger" className="w-6 h-6 rounded object-contain" />
            <span className="font-semibold">MinoLedger</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 MinoLedger. Alle rechten voorbehouden.
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="p-6 rounded-xl border border-border bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, rgba(110,201,152,0.15), rgba(131,135,231,0.15))' }}>
        <span className="text-[#8387e7]">{icon}</span>
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {title}
      </h3>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  );
}
